import type { Logger } from "@expert/logging";

import { Text, TextInput } from "@mantine/core";
import { type JSX, useMemo } from "react";

import type { Contact } from "@/shared-types";

import { isSimulatedVoiceTask, useMostRecentTask } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { DEFAULT_COUNTRY_CODE, formatPhoneNumber, parsePhoneNumber, validatePhoneNumber } from "@/shared-utils";

import styles from "./PhoneNumber.module.css";

interface PhoneNumberProps {
    mdn: string;
}

interface EditablePhoneNumberProps {
    mdn: string;
    customerLeft?: boolean;
}

interface VdnNameProps {
    vdn: Contact;
}

interface QuickConnectLabelProps {
    quickConnectName: string;
}

const anonymousRegExp = /^(?:\+\d+)?(?:anonymous|unknown)$/i;

const unknownMdnFallback = "Participant";

function validateAndFormatMdn(mdn: string, shouldSkipValidation: boolean, logger: Logger) {
    if (shouldSkipValidation) {
        return parsePhoneNumber(mdn).number;
    }

    if (!validatePhoneNumber(mdn).valid) {
        if (!anonymousRegExp.test(mdn)) logger.info(`Unexpected MDN received`);
        return unknownMdnFallback;
    }

    return formatPhoneNumber(mdn, DEFAULT_COUNTRY_CODE, shouldSkipValidation).number;
}

export function PhoneNumber({ mdn }: PhoneNumberProps): JSX.Element {
    const logger = useLogger({ module: "PhoneNumber" });
    const recentTask = useMostRecentTask();
    const isExpertUniversitySimulatedVoiceTask = useMemo(() => isSimulatedVoiceTask(recentTask), [recentTask]);
    const formattedPhoneNumber = validateAndFormatMdn(mdn, isExpertUniversitySimulatedVoiceTask, logger);
    const testId = formattedPhoneNumber === unknownMdnFallback ? "unexpected-phone-number" : "phone-number";

    return <span data-testid={testId}>{formattedPhoneNumber}</span>;
}

export function EditablePhoneNumber({ mdn, customerLeft }: EditablePhoneNumberProps): JSX.Element {
    const logger = useLogger({ module: "PhoneNumber" });
    const recentTask = useMostRecentTask();
    const isExpertUniversitySimulatedVoiceTask = useMemo(() => isSimulatedVoiceTask(recentTask), [recentTask]);
    const defaultText = validateAndFormatMdn(mdn, isExpertUniversitySimulatedVoiceTask, logger);
    const testId = defaultText === unknownMdnFallback ? "unexpected-editable-phone-number" : "editable-phone-number";

    return (
        <TextInput
            className="fs-mask"
            c="white"
            classNames={{ input: styles.phoneNumberTextInput }}
            data-customerleft={customerLeft}
            data-testid={testId}
            defaultValue={defaultText}
            fw="var(--mantine-font-weight-heavy)"
            size="xs"
            variant="unstyled"
        />
    );
}

export function VdnName({ vdn: contact }: VdnNameProps): JSX.Element {
    const hasFriendlyName = contact.group !== "Unlisted"; // TODO: Unlisted VDN's don't have easily understandable names (yet)
    const lastFour = contact.vdn.slice(-4);
    return (
        <Text
            className="fs-mask"
            classNames={{ root: styles.phoneNumberVdnName }}
            data-testid="vdn-name"
            fz="sm"
            fw="var(--mantine-font-weight-heavy)"
        >
            {hasFriendlyName ? contact.name : `Extension ${lastFour}`}
        </Text>
    );
}

export function QuickConnectLabel({ quickConnectName: name }: QuickConnectLabelProps): JSX.Element {
    return (
        <Text
            className="fs-mask"
            classNames={{ root: styles.phoneNumberVdnName }}
            data-testid="vdn-name"
            fz="sm"
            fw="var(--mantine-font-weight-heavy)"
        >
            {name}
        </Text>
    );
}
